import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../store/Index";
import type { Venue, ApiResponse } from "./types/venues.types";
import apiService from "../../service/Api";
import { logout } from "../../store/slice/AuthSlice";
import Pagination from "../../components/common/Pagination";
import { VenueCard } from "./list-venues/VenueCard";
import { VenueSearchBar } from "./list-venues/VenueSearchBar";
import { DeleteConfirmDialog } from "./list-venues/DeleteConfirmationDialog";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const toastConfig = {
  position: "top-right" as const,
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: "light" as const,
  style: {
    backgroundColor: "#fff",
    color: "#333",
    border: "1px solid #C29363",
    zIndex: 9999,
  },
};

export const ListVenues: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [maxcompletenessScore, setCompletenessScore] = useState<
    string | undefined
  >(undefined);
  const [capacity] = useState("");
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [totalItems, setTotalItems] = useState(0);

  const reduxTheme = useSelector((state: RootState) => state.theme);
  const userPermissions = useSelector(
    (state: RootState) => state.auth.user?.permissions || [],
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [venueToDelete, setVenueToDelete] = useState<string | null>(null);

  const hasPermission = (permission: string) => {
    return userPermissions.includes(permission.trim().replace(/'$/, ""));
  };

  // Theme colors
  const primaryColor = reduxTheme.primary_colors?.[400] || "#9c6644";
  const buttonBackgroundColor = reduxTheme.primary_colors?.[400] || "#9c6644";
  const iconButtonBackgroundColor =
    reduxTheme.primary_colors?.[400] || "#4a3228";
  const hoverColor = reduxTheme.primary_colors?.[500] || "#8b5a3c";
  const cancelColor = reduxTheme.neutral_colors?.[100] || "#666";

  const fetchVenues = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage + 1,
        page_size: pageSize,
        search: appliedSearchTerm,
        max_completeness_score: maxcompletenessScore || undefined,
        capacity: capacity || undefined,
      };

      const response = await apiService.get<ApiResponse>(
        "/api/venues/venue/",
        params,
      );

      if (!response.data.results || !Array.isArray(response.data.results)) {
        console.error(
          "API response does not contain results array:",
          response.data,
          response.data,
        );
        toast.error("Unexpected response format from server.", toastConfig);
        setVenues([]);
      } else {
        setVenues(response.data.results);
        setTotalItems(response.data.count);
      }
    } catch (err) {
      let errorMessage = "Failed to load venues. Please try again.";
      const errorResponse = (
        err as {
          response?: { status?: number; data?: { message?: string } };
          message?: string;
        }
      )?.response;

      if (errorResponse?.status === 401) {
        errorMessage = "Session expired. Please log in again.";
        dispatch(logout());
        navigate("/login");
      } else if (errorResponse?.data?.message) {
        errorMessage = errorResponse.data.message;
      } else if (
        (err as { message?: string })?.message?.includes("Network Error")
      ) {
        errorMessage = "Network error. Please check your connection.";
      }
      console.error("Error fetching venues:", err);
      toast.error(errorMessage, toastConfig);
      setVenues([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    appliedSearchTerm,
    capacity,
    currentPage,
    dispatch,
    navigate,
    pageSize,
    maxcompletenessScore,
  ]);

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle search key down (Enter key)
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      setAppliedSearchTerm(searchTerm);
      setCurrentPage(0);
    }
  };

  // Debounced search effect
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (searchTerm !== appliedSearchTerm) {
      debounceTimer.current = setTimeout(() => {
        setAppliedSearchTerm(searchTerm);
        setCurrentPage(0);
      }, 1000);
    }
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [appliedSearchTerm, searchTerm]);

  // Fetch venues when pagination or search changes
  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  // Handle filter changes
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setCurrentPage(0);
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [capacity, pageSize, maxcompletenessScore]);

  const confirmDelete = (id: string) => {
    setVenueToDelete(id);
    setOpenDeleteDialog(true);
  };

  const handleDeleteVenue = async () => {
    if (venueToDelete === null) return;

    try {
      await apiService.delete(`/api/venues/venue/${venueToDelete}/`);
      toast.success("Venue deleted successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setOpenDeleteDialog(false);
      setVenueToDelete(null);
      fetchVenues();
    } catch (err) {
      let errorMessage = "Failed to delete venue.";
      const errorData = (
        err as { response?: { data?: { detail?: string; message?: string } } }
      )?.response?.data;
      if (errorData?.detail) {
        errorMessage = errorData.detail;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Delete venue error:", err);
      setOpenDeleteDialog(false);
      setVenueToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setVenueToDelete(null);
  };

  return (
    <Box>
      <VenueSearchBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchInputChange}
        onSearchKeyDown={handleSearchKeyDown}
        onAddVenue={() => navigate("/Venues/add")}
        canAddVenue={hasPermission("venue.add_venue")}
        primaryColor={primaryColor}
        backgroundColor={reduxTheme.primary_colors?.[900]}
        onTogglePrivateResidence={(enabled) => {
          setSearchTerm((prev) => {
            const trimmed = prev.replace(/\bprivate\b/gi, "").trim();
            return enabled
              ? trimmed
                ? `${trimmed} private`
                : "private"
              : trimmed;
          });
          setCurrentPage(0);
        }}
        onToggleCompleteness={(enabled) => {
          setCompletenessScore(enabled ? "50" : undefined);
          setCurrentPage(0);
        }}
      />

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: "#614438" }} />
        </Box>
      ) : venues.length === 0 ? (
        <Typography variant="h6" color="text.secondary" textAlign="center">
          No venues found.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {venues.map((venue) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={venue.id}>
              <VenueCard
                venue={venue}
                onDelete={confirmDelete}
                canEdit={hasPermission("venue.change_venue")}
                canDelete={hasPermission("venue.delete_venue")}
                canView={hasPermission("venue.view_venue")}
                primaryColor={primaryColor}
                buttonBackgroundColor={buttonBackgroundColor}
                iconButtonBackgroundColor={iconButtonBackgroundColor}
                hoverColor={hoverColor}
                googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                onCoverUpdated={(newCoverUrl: string) => {
                  setVenues((prev) =>
                    prev.map((v) =>
                      v.id === venue.id
                        ? {
                            ...v,
                            files: v.files?.map((f) =>
                              f.id ===
                              venue.files?.find((f) => f.file === newCoverUrl)
                                ?.id
                                ? { ...f }
                                : f,
                            ),
                          }
                        : v,
                    ),
                  );
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {!isLoading && venues.length > 0 && (
        <Pagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalItems}
          onPageChange={(page) => setCurrentPage(page)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(0);
          }}
        />
      )}

      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteVenue}
        cancelColor={cancelColor}
      />
    </Box>
  );
};

export default ListVenues;
